import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import BookingForm from '../components/BookingForm';
import SEO from '../components/SEO';

export default function TreatmentDetail() {
  const { slug } = useParams();
  const { lang, getLoc } = useLanguage();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTreatment() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/treatments');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            const found = data.find((item) => {
              const s1 = typeof item.treatment_slug === 'object' ? item.treatment_slug[lang] || item.treatment_slug.da : item.treatment_slug;
              const s2 = typeof item.slug === 'object' ? item.slug[lang] || item.slug.da : item.slug;
              return s1 === slug || s2 === slug || slug.includes(s1) || (s1 && s1.includes(slug));
            });
            if (found) {
              setTreatment(found);
              setLoading(false);
              return;
            }
          }
        }
      } catch (e) {
        // Fallback
      }

      // Local fallback dictionary for all 6 treatments
      const fallbackMap = {
        'deep-tissue-massage': {
          title: { da: 'Bindevævsmassage', en: 'Deep Tissue Massage' },
          intro: {
            da: '<p>Bindevævsmassage er en målrettet og fysiologisk behandling, der løsner kroniske spændinger, myoser og stivhed i muskler og fascia.</p>',
            en: '<p>Deep tissue massage is a targeted physiological technique designed to release chronic tension, knots, and joint stiffness.</p>'
          },
          who_is_it_for: {
            da: '<p>Ideel til dig, der arbejder foran computeren, har tungt fysisk arbejde eller oplever tilbagevendende rygsmerter og spændingshovedpine.</p>',
            en: '<p>Ideal for office workers, physically active individuals, or anyone experiencing persistent back pain and tension headaches.</p>'
          },
          benefits: {
            da: '<p>• Løsner dybe myoser og muskelknuder<br/>• Øger blodcirkulation og ilttilførsel<br/>• Forbedrer ledfleksibilitet og kropsholdning</p>',
            en: '<p>• Releases deep muscle knots<br/>• Improves circulation and tissue oxygenation<br/>• Enhances joint flexibility and posture</p>'
          },
          how_it_works: {
            da: '<p>Behandlingen indledes med en kort samtale om dine fokusområder. Derefter arbejdes der langsomt og kontrolleret i dybden af muskelfibrene med rolige tryk og strøg.</p>',
            en: '<p>We begin with a short dialogue about your areas of tension. Controlled, deep pressure is applied slowly to target deeper muscle layers.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/deep-tissue.jpg'],
          duration: 60,
          price: 700
        },
        'neuromuscular-massage': {
          title: { da: 'Neuromuskulær massage', en: 'Neuromuscular Massage' },
          intro: {
            da: '<p>En specialiseret, dybdegående manuel behandlingsform, der afbalancerer samspillet mellem nervesystemet og musklerne for effektiv smertelindring.</p>',
            en: '<p>A specialized, in-depth manual therapy that balances the interaction between the nervous system and muscles for effective pain relief.</p>'
          },
          who_is_it_for: {
            da: '<p>Særligt velegnet til dig med stædige triggerpunkter, tilbagevendende hovedpine, udstrålende smerter eller kroniske spændingstilstande.</p>',
            en: '<p>Particularly suited for individuals with stubborn trigger points, tension headaches, radiating pain, or chronic tension states.</p>'
          },
          benefits: {
            da: '<p>• Opløser dybe triggerpunkter og muskelspasmer<br/>• Lindrer udstrålende nervesmerter og tryk<br/>• Genskaber naturlig mobilitet og bevægelsesfrihed</p>',
            en: '<p>• Releases deep trigger points and muscle spasms<br/>• Relieves radiating nerve pain and compression<br/>• Restores natural mobility and ease of movement</p>'
          },
          how_it_works: {
            da: '<p>Ved hjælp af præcise, statiske fingertryk på aktive triggerpunkter og kontrollerede strøg afspændes overbelastede muskelfibre og nervebaner nænsomt.</p>',
            en: '<p>Using precise static pressure on active trigger points and controlled strokes, strained muscle fibers and nerve pathways are gently released.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/neuromuscular-therapy-treatment.jpg'],
          duration: 60,
          price: 700
        },
        'bio-dynamic-craniosacral-therapy': {
          title: { da: 'Biodynamisk Kranio-Sakral Terapi', en: 'Bio Dynamic Craniosacral Therapy' },
          intro: {
            da: '<p>En dybdegående og yderst blid manuel behandlingsform, der støtter det centrale nervesystem og kroppens egen selvregulering.</p>',
            en: '<p>A subtle, gentle manual therapy supporting the central nervous system and the body’s innate self-healing wisdom.</p>'
          },
          who_is_it_for: {
            da: '<p>Velegnet ved stress, angst, søvnløshed, hovedpine, piskesmæld eller til dig, der har brug for dyb nulstilling.</p>',
            en: '<p>Beneficial for stress, insomnia, headaches, whiplash, or anyone seeking deep nervous system recalibration.</p>'
          },
          benefits: {
            da: '<p>• Beroliger det parasympatiske nervesystem<br/>• Dæmper stress og uro i kroppen<br/>• Fremmer dyb søvn og regenerering</p>',
            en: '<p>• Calms the parasympathetic nervous system<br/>• Reduces anxiety and stress<br/>• Promotes deep restorative sleep</p>'
          },
          how_it_works: {
            da: '<p>Du ligger fuldt påklædt på briksen, mens behandleren med helt lette berøringer på kranie, rygsøjle og korsben lytter til kroppens biodynamiske rytmer.</p>',
            en: '<p>Fully clothed on the table, gentle contacts on the cranium, spine, and sacrum tune into your body’s fluid rhythms.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/craniosacral.jpg'],
          duration: 60,
          price: 700
        },
        'pregnancy-massage': {
          title: { da: 'Gravidmassage', en: 'Pregnancy Massage' },
          intro: {
            da: '<p>Specialiseret og omsorgsfuld kropsbehandling tilpasset den gravides krop og behov gennem alle graviditetens trimestre.</p>',
            en: '<p>Specialized, nurturing bodywork customized to the pregnant mother’s body through every trimester.</p>'
          },
          who_is_it_for: {
            da: '<p>Til gravide, der oplever bækkenbundsspændinger, lændesmerter, ødemer i benene eller blot har brug for ro og omsorg.</p>',
            en: '<p>For expectant mothers experiencing pelvic tightness, lower back ache, swollen feet, or seeking restful self-care.</p>'
          },
          benefits: {
            da: '<p>• Aflaster lænd, ryg og bækken<br/>• Mindsker væskeophobning<br/>• Giver dyb ro og overskud til den kommende mor</p>',
            en: '<p>• Relieves lower back and pelvic pressure<br/>• Reduces fluid swelling<br/>• Restores energy and peace</p>'
          },
          how_it_works: {
            da: '<p>Behandlingen foregår i behagelige sideleje-positioner med støttende graviditetspuder for optimal komfort for mor og baby.</p>',
            en: '<p>Performed in side-lying positions with specialized supportive cushions ensuring ultimate comfort for mother and baby.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/pregnancy-massage.jpg'],
          duration: 60,
          price: 700
        },
        'post-partum-massage': {
          title: { da: 'Efterfødselsmassage', en: 'Post Partum Massage' },
          intro: {
            da: '<p>Gendannende kropsmassage til nye mødre, der støtter kroppens fysiske heling og giver ny næring efter fødslen.</p>',
            en: '<p>Restorative postnatal bodywork supporting physical healing and rebuilding stamina for new mothers.</p>'
          },
          who_is_it_for: {
            da: '<p>Til nybagte mødre uger og måneder efter fødslen, som oplever ømme skuldre fra amning og tunge løft.</p>',
            en: '<p>For mothers weeks or months postpartum dealing with shoulder strain from nursing and carrying your baby.</p>'
          },
          benefits: {
            da: '<p>• Løsner amme- og bærespændinger<br/>• Støtter bækken og mavevævets heling<br/>• Giver mor et tiltrængt pusterum</p>',
            en: '<p>• Releases nursing and carrying tension<br/>• Supports pelvic recovery<br/>• Provides essential mental & physical rest</p>'
          },
          how_it_works: {
            da: '<p>Kombinerer blid afspænding af skuldre, ryg og bækken med støttende greb, tilpasset din krops stadie efter fødslen.</p>',
            en: '<p>Combines gentle muscle relief for shoulders, back, and pelvis tailored to your postnatal stage.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09348_2.jpg'],
          duration: 75,
          price: 1050
        },
        'sports-massage': {
          title: { da: 'Sportsmassage', en: 'Sports Massage' },
          intro: {
            da: '<p>Effektiv og energisk massage, der fremskynder muskelrestitution og forebygger sportsskader.</p>',
            en: '<p>Effective sports massage designed to accelerate muscle recovery and prevent athletic injuries.</p>'
          },
          who_is_it_for: {
            da: '<p>For løbere, motionister, atleter og aktive mennesker, der ønsker at yde optimalt og undgå overbelastning.</p>',
            en: '<p>For runners, fitness enthusiasts, and athletes wanting optimal muscle performance and injury prevention.</p>'
          },
          benefits: {
            da: '<p>• Øger muskelrestitution<br/>• Forbedrer smidighed og ydeevne<br/>• Forebygger overbelastningsskader</p>',
            en: '<p>• Accelerates muscle recovery<br/>• Enhances mobility & performance<br/>• Prevents overuse injuries</p>'
          },
          how_it_works: {
            da: '<p>Teknikken kombinerer faste strøg, stræk og friktioner på belastede muskelgrupper.</p>',
            en: '<p>Combines firm strokes, muscle stretches, and targeted friction on stressed muscle groups.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/sports-massage.jpg'],
          duration: 60,
          price: 700
        },
        'wellness-massage': {
          title: { da: 'Velværemassage', en: 'Wellness Massage' },
          intro: {
            da: '<p>En rolig, nærværende og dybt afspændende velværemassage designet til at give slip på hverdagens travlhed og genopbygge indre ro.</p>',
            en: '<p>A tranquil, nurturing, and deeply relaxing wellness massage designed to release daily stress and restore inner peace.</p>'
          },
          who_is_it_for: {
            da: '<p>Til dig, der trænger til en pause fra hverdagens stress, forkælelse af kroppen og en oplevelse af ren ro og velvære.</p>',
            en: '<p>For anyone in need of a restful break from daily stress, bodily pampering, and pure relaxation.</p>'
          },
          benefits: {
            da: '<p>• Afstresser sindet og beroliger nervesystemet<br/>• Løsner hverdagsspændinger i hele kroppen<br/>• Skaber dyb indre ro og fornyet velvære</p>',
            en: '<p>• Calms the mind and soothes the nervous system<br/>• Releases daily tension throughout the body<br/>• Restores inner peace and overall well-being</p>'
          },
          how_it_works: {
            da: '<p>Blide, rytmiske strøg og lette æltninger med økologiske olier, der skaber dyb afspænding fra top til tå i trygge og rolige rammer.</p>',
            en: '<p>Gentle, rhythmic strokes and soft kneading using organic oils that induce whole-body relaxation from head to toe in peaceful surroundings.</p>'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/wellness-massage.jpg'],
          duration: 60,
          price: 700
        }
      };

      const matchKey = Object.keys(fallbackMap).find((k) => slug.includes(k) || k.includes(slug)) || 'deep-tissue-massage';
      setTreatment(fallbackMap[matchKey]);
      setLoading(false);
    }

    loadTreatment();
  }, [slug, lang]);

  if (loading) {
    return (
      <div className="py-24 text-center text-[#6E6760]">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-[#2E3191]" />
      </div>
    );
  }

  if (!treatment) {
    return (
      <div className="py-24 text-center space-y-4">
        <h2 className="font-serif-display text-2xl font-bold">Behandlingen blev ikke fundet</h2>
        <Link to="/behandlinger" className="text-[#2E3191] font-semibold underline">
          Se alle behandlinger →
        </Link>
      </div>
    );
  }

  const titleStr = getLoc(treatment.title);
  const imgUrl = Array.isArray(treatment.image) && treatment.image[0] ? treatment.image[0] : 'https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/deep-tissue.jpg';

  return (
    <div className="space-y-0">
      <SEO
        title={titleStr}
        description={getLoc(treatment.short_description) || `${titleStr} hos Massage with Jette i København.`}
      />
      
      {/* Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <Link to="/behandlinger" className="text-xs font-semibold text-[#2E3191] uppercase tracking-wider hover:underline">
            ← {lang === 'da' ? 'Tilbage til behandlinger' : 'Back to treatments'}
          </Link>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {titleStr}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm font-semibold text-[#2E3191] pt-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E8E0D7]">
              <i className="fa-regular fa-clock" /> 60 – 120 min
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E8E0D7]">
              <i className="fa-solid fa-tag" /> {lang === 'da' ? 'Fra 700 DKK (baseret på tid)' : 'From 700 DKK (time-based)'}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EEF0F8] border border-[#2E3191]/20">
              <i className="fa-solid fa-certificate" /> {lang === 'da' ? 'RAB-godkendt' : 'RAB Approved'}
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-10">
              
              <div className="aspect-16/9 rounded-2xl overflow-hidden border border-[#E8E0D7] shadow-sm">
                <img
                  src={imgUrl}
                  alt={titleStr}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Intro */}
              <div className="space-y-3">
                <h2 className="font-serif-display text-2xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Om behandlingen' : 'About the treatment'}
                </h2>
                <div
                  className="text-base text-[#6E6760] leading-relaxed space-y-2"
                  dangerouslySetInnerHTML={{ __html: getLoc(treatment.intro) }}
                />
              </div>

              {/* Who is it for */}
              <div className="p-6 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] space-y-3">
                <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Hvem er behandlingen til?' : 'Who is this treatment for?'}
                </h3>
                <div
                  className="text-sm text-[#6E6760] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getLoc(treatment.who_is_it_for) }}
                />
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Fordele ved behandlingen' : 'Benefits'}
                </h3>
                <div
                  className="text-sm text-[#6E6760] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getLoc(treatment.benefits) }}
                />
              </div>

              {/* How it works */}
              <div className="space-y-3">
                <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Hvordan foregår en behandling?' : 'How the session works'}
                </h3>
                <div
                  className="text-sm text-[#6E6760] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getLoc(treatment.how_it_works) }}
                />
              </div>

              {/* Time-based Pricing & RAB Info Box */}
              <div className="p-6 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                    {lang === 'da' ? 'Priser baseret på tid' : 'Time-based Pricing'}
                  </h3>
                  <span className="text-[11px] font-semibold text-[#2E3191] bg-[#EEF0F8] px-2.5 py-1 rounded-md border border-[#2E3191]/20">
                    RAB Godkendt
                  </span>
                </div>
                <p className="text-xs text-[#6E6760]">
                  {lang === 'da'
                    ? 'Alle behandlinger er baseret på tid, så du og Jette kan tilpasse sessionen præcist efter dine ønsker og kroppens behov.'
                    : 'All treatments are time-based, allowing you and Jette to tailor the session to your immediate physical needs.'}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 text-center">
                  <div className="p-3 bg-white rounded-xl border border-[#E8E0D7]">
                    <span className="block text-[11px] font-semibold text-[#2E3191]">60 min</span>
                    <strong className="block text-base font-bold text-[#2C2825]">700 DKK</strong>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-[#E8E0D7]">
                    <span className="block text-[11px] font-semibold text-[#2E3191]">75 min</span>
                    <strong className="block text-base font-bold text-[#2C2825]">860 DKK</strong>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-[#E8E0D7]">
                    <span className="block text-[11px] font-semibold text-[#2E3191]">90 min</span>
                    <strong className="block text-base font-bold text-[#2C2825]">1.000 DKK</strong>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-[#E8E0D7]">
                    <span className="block text-[11px] font-semibold text-[#2E3191]">120 min</span>
                    <strong className="block text-base font-bold text-[#2C2825]">1.300 DKK</strong>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-[#EEF0F8] border border-[#2E3191]/20 text-xs text-[#2C2825] flex items-center gap-2">
                  <i className="fa-solid fa-tags text-[#2E3191] shrink-0" />
                  <span><strong>Spar 15%</strong> ved køb af 5 behandlinger -- udløber efter 1 år.</span>
                </div>

                <div className="pt-1">
                  <a
                    href="https://massage-with-jette.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-4 rounded-xl bg-[#2E3191] hover:bg-[#1E2063] text-white text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-xs"
                  >
                    <span>{lang === 'da' ? 'Book tid til behandlingen på Planway' : 'Book this treatment on Planway'}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                  </a>
                </div>

                <p className="text-[11px] text-[#6E6760] italic">
                  Jette er RAB godkendt - Anerkendt af sundhedsvæsenet
                </p>
              </div>

            </div>

            {/* Right Sticky Booking Form */}
            <div className="lg:col-span-5 sticky top-28">
              <BookingForm defaultTreatment={`${titleStr} (${treatment.duration} min)`} />
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
